export interface ChemicalElement {
  symbol: string;
  name: string;
  atomic_number: number;
  category: 'PRECIOUS_METALS' | 'BASE_METALS' | 'RARE_EARTH' | 'MAJOR_ELEMENTS' | 'TRACE_ELEMENTS' | 'ACTINIDES' | 'ALKALI_METALS' | 'ALKALINE_EARTH' | 'HALOGENS' | 'NOBLE_GASES' | 'METALLOIDS' | 'TRANSITION_METALS' | 'OTHER_NONMETALS';
  common_in_mining: boolean;
  has_methods?: boolean; // NEW: Track if element has available methods
}

export interface ElementGroup {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  elements: ChemicalElement[];
  priority: number; // For display order
}

export interface MethodForElement {
  method_code: string;
  method_name: string;
  laboratory: string;
  is_accredited: boolean;
  typical_tat_hours?: number;
  description?: string;
}

export interface ElementMethodMatch {
  element: ChemicalElement;
  availableMethods: MethodForElement[];
  recommendedMethod?: MethodForElement;
}

export class ElementGroupsService {
  // COMPREHENSIVE chemical elements database - ALL ELEMENTS for future method expansion
  private readonly elements: ChemicalElement[] = [
    // PRECIOUS METALS (High priority for mining)
    { symbol: 'Au', name: 'Gold (Or)', atomic_number: 79, category: 'PRECIOUS_METALS', common_in_mining: true, has_methods: true },
    { symbol: 'Ag', name: 'Silver (Argent)', atomic_number: 47, category: 'PRECIOUS_METALS', common_in_mining: true, has_methods: true },
    { symbol: 'Pt', name: 'Platinum (Platine)', atomic_number: 78, category: 'PRECIOUS_METALS', common_in_mining: true, has_methods: false },
    { symbol: 'Pd', name: 'Palladium', atomic_number: 46, category: 'PRECIOUS_METALS', common_in_mining: true, has_methods: false },
    { symbol: 'Rh', name: 'Rhodium', atomic_number: 45, category: 'PRECIOUS_METALS', common_in_mining: false, has_methods: false },
    { symbol: 'Ru', name: 'Ruthenium', atomic_number: 44, category: 'PRECIOUS_METALS', common_in_mining: false, has_methods: false },
    { symbol: 'Ir', name: 'Iridium', atomic_number: 77, category: 'PRECIOUS_METALS', common_in_mining: false, has_methods: false },
    { symbol: 'Os', name: 'Osmium', atomic_number: 76, category: 'PRECIOUS_METALS', common_in_mining: false, has_methods: false },
    
    // BASE METALS (Essential for mining industry)
    { symbol: 'Cu', name: 'Copper (Cuivre)', atomic_number: 29, category: 'BASE_METALS', common_in_mining: true, has_methods: true },
    { symbol: 'Pb', name: 'Lead (Plomb)', atomic_number: 82, category: 'BASE_METALS', common_in_mining: true, has_methods: true },
    { symbol: 'Zn', name: 'Zinc', atomic_number: 30, category: 'BASE_METALS', common_in_mining: true, has_methods: true },
    { symbol: 'Ni', name: 'Nickel', atomic_number: 28, category: 'BASE_METALS', common_in_mining: true, has_methods: true },
    { symbol: 'Co', name: 'Cobalt', atomic_number: 27, category: 'BASE_METALS', common_in_mining: true, has_methods: true },
    { symbol: 'Sn', name: 'Tin (Étain)', atomic_number: 50, category: 'BASE_METALS', common_in_mining: true, has_methods: true },
    { symbol: 'Al', name: 'Aluminum (Aluminium)', atomic_number: 13, category: 'BASE_METALS', common_in_mining: true, has_methods: true },
    
    // MAJOR ELEMENTS (Oxides and major constituents)
    { symbol: 'Fe', name: 'Iron (Fer)', atomic_number: 26, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Ca', name: 'Calcium', atomic_number: 20, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Mg', name: 'Magnesium (Magnésium)', atomic_number: 12, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Si', name: 'Silicon (Silicium)', atomic_number: 14, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'K', name: 'Potassium (Potassium)', atomic_number: 19, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Na', name: 'Sodium (Sodium)', atomic_number: 11, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Ti', name: 'Titanium (Titane)', atomic_number: 22, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Mn', name: 'Manganese (Manganèse)', atomic_number: 25, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'P', name: 'Phosphorus (Phosphore)', atomic_number: 15, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'S', name: 'Sulfur (Soufre)', atomic_number: 16, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    
    // TRACE ELEMENTS (Important for mining analysis)
    { symbol: 'As', name: 'Arsenic', atomic_number: 33, category: 'TRACE_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Sb', name: 'Antimony (Antimoine)', atomic_number: 51, category: 'TRACE_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Bi', name: 'Bismuth', atomic_number: 83, category: 'TRACE_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Cd', name: 'Cadmium', atomic_number: 48, category: 'TRACE_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Hg', name: 'Mercury (Mercure)', atomic_number: 80, category: 'TRACE_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Se', name: 'Selenium (Sélénium)', atomic_number: 34, category: 'TRACE_ELEMENTS', common_in_mining: true, has_methods: false },
    { symbol: 'Te', name: 'Tellurium (Tellure)', atomic_number: 52, category: 'TRACE_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Mo', name: 'Molybdenum (Molybdène)', atomic_number: 42, category: 'TRACE_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'W', name: 'Tungsten (Tungstène)', atomic_number: 74, category: 'TRACE_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'V', name: 'Vanadium', atomic_number: 23, category: 'TRACE_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Cr', name: 'Chromium (Chrome)', atomic_number: 24, category: 'TRACE_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Ba', name: 'Barium', atomic_number: 56, category: 'TRACE_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Sr', name: 'Strontium', atomic_number: 38, category: 'TRACE_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Be', name: 'Beryllium (Béryllium)', atomic_number: 4, category: 'TRACE_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Li', name: 'Lithium', atomic_number: 3, category: 'TRACE_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Rb', name: 'Rubidium', atomic_number: 37, category: 'TRACE_ELEMENTS', common_in_mining: false, has_methods: true },
    { symbol: 'Cs', name: 'Cesium (Césium)', atomic_number: 55, category: 'TRACE_ELEMENTS', common_in_mining: false, has_methods: false },
    { symbol: 'Ga', name: 'Gallium', atomic_number: 31, category: 'TRACE_ELEMENTS', common_in_mining: false, has_methods: true },
    { symbol: 'Ge', name: 'Germanium', atomic_number: 32, category: 'TRACE_ELEMENTS', common_in_mining: false, has_methods: true },
    { symbol: 'In', name: 'Indium', atomic_number: 49, category: 'TRACE_ELEMENTS', common_in_mining: false, has_methods: true },
    { symbol: 'Tl', name: 'Thallium', atomic_number: 81, category: 'TRACE_ELEMENTS', common_in_mining: false, has_methods: false },
    { symbol: 'Sc', name: 'Scandium', atomic_number: 21, category: 'TRACE_ELEMENTS', common_in_mining: false, has_methods: true },
    
    // TRANSITION METALS (Additional mining-relevant metals)
    { symbol: 'Zr', name: 'Zirconium', atomic_number: 40, category: 'TRANSITION_METALS', common_in_mining: true, has_methods: true },
    { symbol: 'Hf', name: 'Hafnium', atomic_number: 72, category: 'TRANSITION_METALS', common_in_mining: false, has_methods: false },
    { symbol: 'Nb', name: 'Niobium', atomic_number: 41, category: 'TRANSITION_METALS', common_in_mining: true, has_methods: true },
    { symbol: 'Ta', name: 'Tantalum (Tantale)', atomic_number: 73, category: 'TRANSITION_METALS', common_in_mining: true, has_methods: true },
    { symbol: 'Re', name: 'Rhenium (Rhénium)', atomic_number: 75, category: 'TRANSITION_METALS', common_in_mining: false, has_methods: false },
    
    // RARE EARTH ELEMENTS (Growing importance in mining)
    { symbol: 'La', name: 'Lanthanum (Lanthane)', atomic_number: 57, category: 'RARE_EARTH', common_in_mining: false, has_methods: false },
    { symbol: 'Ce', name: 'Cerium (Cérium)', atomic_number: 58, category: 'RARE_EARTH', common_in_mining: false, has_methods: false },
    { symbol: 'Pr', name: 'Praseodymium', atomic_number: 59, category: 'RARE_EARTH', common_in_mining: false, has_methods: false },
    { symbol: 'Nd', name: 'Neodymium (Néodyme)', atomic_number: 60, category: 'RARE_EARTH', common_in_mining: false, has_methods: false },
    { symbol: 'Pm', name: 'Promethium (Prométhium)', atomic_number: 61, category: 'RARE_EARTH', common_in_mining: false, has_methods: false },
    { symbol: 'Sm', name: 'Samarium', atomic_number: 62, category: 'RARE_EARTH', common_in_mining: false, has_methods: false },
    { symbol: 'Eu', name: 'Europium', atomic_number: 63, category: 'RARE_EARTH', common_in_mining: false, has_methods: false },
    { symbol: 'Gd', name: 'Gadolinium', atomic_number: 64, category: 'RARE_EARTH', common_in_mining: false, has_methods: false },
    { symbol: 'Tb', name: 'Terbium', atomic_number: 65, category: 'RARE_EARTH', common_in_mining: false, has_methods: false },
    { symbol: 'Dy', name: 'Dysprosium', atomic_number: 66, category: 'RARE_EARTH', common_in_mining: false, has_methods: false },
    { symbol: 'Ho', name: 'Holmium', atomic_number: 67, category: 'RARE_EARTH', common_in_mining: false, has_methods: false },
    { symbol: 'Er', name: 'Erbium', atomic_number: 68, category: 'RARE_EARTH', common_in_mining: false, has_methods: false },
    { symbol: 'Tm', name: 'Thulium', atomic_number: 69, category: 'RARE_EARTH', common_in_mining: false, has_methods: false },
    { symbol: 'Yb', name: 'Ytterbium', atomic_number: 70, category: 'RARE_EARTH', common_in_mining: false, has_methods: false },
    { symbol: 'Lu', name: 'Lutetium (Lutécium)', atomic_number: 71, category: 'RARE_EARTH', common_in_mining: false, has_methods: false },
    { symbol: 'Y', name: 'Yttrium', atomic_number: 39, category: 'RARE_EARTH', common_in_mining: false, has_methods: true },
    
    // ACTINIDES (Uranium and thorium relevant for some mining)
    { symbol: 'U', name: 'Uranium', atomic_number: 92, category: 'ACTINIDES', common_in_mining: true, has_methods: true },
    { symbol: 'Th', name: 'Thorium', atomic_number: 90, category: 'ACTINIDES', common_in_mining: true, has_methods: true },
    { symbol: 'Ac', name: 'Actinium', atomic_number: 89, category: 'ACTINIDES', common_in_mining: false, has_methods: false },
    { symbol: 'Pa', name: 'Protactinium', atomic_number: 91, category: 'ACTINIDES', common_in_mining: false, has_methods: false },
    
    // OTHER IMPORTANT ELEMENTS
    { symbol: 'B', name: 'Boron (Bore)', atomic_number: 5, category: 'METALLOIDS', common_in_mining: true, has_methods: false },
    { symbol: 'C', name: 'Carbon (Carbone)', atomic_number: 6, category: 'OTHER_NONMETALS', common_in_mining: true, has_methods: false },
    { symbol: 'N', name: 'Nitrogen (Azote)', atomic_number: 7, category: 'OTHER_NONMETALS', common_in_mining: false, has_methods: false },
    { symbol: 'O', name: 'Oxygen (Oxygène)', atomic_number: 8, category: 'OTHER_NONMETALS', common_in_mining: false, has_methods: false },
    { symbol: 'F', name: 'Fluorine (Fluor)', atomic_number: 9, category: 'HALOGENS', common_in_mining: false, has_methods: false },
    { symbol: 'Cl', name: 'Chlorine (Chlore)', atomic_number: 17, category: 'HALOGENS', common_in_mining: false, has_methods: false },
    { symbol: 'Br', name: 'Bromine (Brome)', atomic_number: 35, category: 'HALOGENS', common_in_mining: false, has_methods: false },
    { symbol: 'I', name: 'Iodine (Iode)', atomic_number: 53, category: 'HALOGENS', common_in_mining: false, has_methods: false },
    
    // NOBLE GASES (Generally not relevant for mining but included for completeness)
    { symbol: 'He', name: 'Helium (Hélium)', atomic_number: 2, category: 'NOBLE_GASES', common_in_mining: false, has_methods: false },
    { symbol: 'Ne', name: 'Neon (Néon)', atomic_number: 10, category: 'NOBLE_GASES', common_in_mining: false, has_methods: false },
    { symbol: 'Ar', name: 'Argon', atomic_number: 18, category: 'NOBLE_GASES', common_in_mining: false, has_methods: false },
    { symbol: 'Kr', name: 'Krypton', atomic_number: 36, category: 'NOBLE_GASES', common_in_mining: false, has_methods: false },
    { symbol: 'Xe', name: 'Xenon (Xénon)', atomic_number: 54, category: 'NOBLE_GASES', common_in_mining: false, has_methods: false },
    { symbol: 'Rn', name: 'Radon', atomic_number: 86, category: 'NOBLE_GASES', common_in_mining: false, has_methods: false },
    
    // ALKALI METALS
    { symbol: 'H', name: 'Hydrogen (Hydrogène)', atomic_number: 1, category: 'OTHER_NONMETALS', common_in_mining: false, has_methods: false },
    { symbol: 'Fr', name: 'Francium', atomic_number: 87, category: 'ALKALI_METALS', common_in_mining: false, has_methods: false },
    
    // ALKALINE EARTH METALS
    { symbol: 'Ra', name: 'Radium', atomic_number: 88, category: 'ALKALINE_EARTH', common_in_mining: false, has_methods: false },
    
    // 🆕 COMPOUND ELEMENTS (Oxides, Carbonates, Fluorides) - Not in periodic table but selectable
    { symbol: 'Al2O3', name: 'Oxyde d\'aluminium (Alumine)', atomic_number: 0, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'CaO', name: 'Oxyde de calcium (Chaux vive)', atomic_number: 0, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Fe2O3', name: 'Oxyde de fer (Hématite)', atomic_number: 0, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'K2O', name: 'Oxyde de potassium', atomic_number: 0, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'MgO', name: 'Oxyde de magnésium (Magnésie)', atomic_number: 0, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'MnO', name: 'Oxyde de manganèse', atomic_number: 0, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'Na2O', name: 'Oxyde de sodium', atomic_number: 0, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'P2O5', name: 'Pentoxyde de phosphore', atomic_number: 0, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'SiO2', name: 'Dioxyde de silicium (Silice)', atomic_number: 0, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'TiO2', name: 'Dioxyde de titane', atomic_number: 0, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'CaCO3', name: 'Carbonate de calcium (Calcite)', atomic_number: 0, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
    { symbol: 'CaF2', name: 'Fluorure de calcium (Fluorine)', atomic_number: 0, category: 'MAJOR_ELEMENTS', common_in_mining: true, has_methods: true },
  ];

  // Organized element groups for intuitive selection
  private readonly elementGroups: ElementGroup[] = [
    {
      id: 'precious_metals',
      name: 'Métaux Précieux',
      description: 'Or, Argent, Platine et autres métaux précieux',
      icon: '💎',
      color: '#FFD700',
      priority: 1,
      elements: this.elements.filter(e => e.category === 'PRECIOUS_METALS')
    },
    {
      id: 'base_metals',
      name: 'Métaux de Base',
      description: 'Cuivre, Plomb, Zinc, Nickel, Cobalt, Aluminium',
      icon: '🔧',
      color: '#FF6B35',
      priority: 2,
      elements: this.elements.filter(e => e.category === 'BASE_METALS')
    },
    {
      id: 'major_elements',
      name: 'Éléments Majeurs',
      description: 'Fer, Calcium, Silicium et oxydes majeurs',
      icon: '⚗️',
      color: '#4A90E2',
      priority: 3,
      elements: this.elements.filter(e => e.category === 'MAJOR_ELEMENTS')
    },
    {
      id: 'trace_elements',
      name: 'Éléments Traces',
      description: 'Arsenic, Mercure, Cadmium et autres traces',
      icon: '🔬',
      color: '#8E44AD',
      priority: 4,
      elements: this.elements.filter(e => e.category === 'TRACE_ELEMENTS')
    },
    {
      id: 'transition_metals',
      name: 'Métaux de Transition',
      description: 'Zirconium, Niobium, Tantale et autres métaux de transition',
      icon: '⚡',
      color: '#E67E22',
      priority: 5,
      elements: this.elements.filter(e => e.category === 'TRANSITION_METALS')
    },
    {
      id: 'rare_earth',
      name: 'Terres Rares',
      description: 'Lanthanides et éléments de terres rares',
      icon: '🌍',
      color: '#27AE60',
      priority: 6,
      elements: this.elements.filter(e => e.category === 'RARE_EARTH')
    },
    {
      id: 'actinides',
      name: 'Actinides',
      description: 'Uranium, Thorium et autres actinides',
      icon: '☢️',
      color: '#E74C3C',
      priority: 7,
      elements: this.elements.filter(e => e.category === 'ACTINIDES')
    },
    {
      id: 'other_elements',
      name: 'Autres Éléments',
      description: 'Métalloïdes, halogènes, gaz nobles et autres éléments',
      icon: '🧪',
      color: '#95A5A6',
      priority: 8,
      elements: this.elements.filter(e => 
        ['METALLOIDS', 'HALOGENS', 'NOBLE_GASES', 'ALKALI_METALS', 'ALKALINE_EARTH', 'OTHER_NONMETALS'].includes(e.category)
      )
    }
  ];

  /**
   * Get all element groups organized for UI
   */
  getElementGroups(): ElementGroup[] {
    return this.elementGroups.sort((a, b) => a.priority - b.priority);
  }

  /**
   * Get elements by category
   */
  getElementsByCategory(category: ChemicalElement['category']): ChemicalElement[] {
    return this.elements.filter(e => e.category === category);
  }

  /**
   * Get most commonly requested elements in mining
   */
  getCommonMiningElements(): ChemicalElement[] {
    return this.elements.filter(e => e.common_in_mining).sort((a, b) => a.atomic_number - b.atomic_number);
  }

  /**
   * Get all available elements (for future method expansion)
   */
  getAllElements(): ChemicalElement[] {
    return this.elements.sort((a, b) => a.atomic_number - b.atomic_number);
  }

  /**
   * Get elements that currently have available methods
   */
  getElementsWithMethods(): ChemicalElement[] {
    return this.elements.filter(e => e.has_methods).sort((a, b) => a.symbol.localeCompare(b.symbol));
  }

  /**
   * Get elements without methods (for future expansion)
   */
  getElementsWithoutMethods(): ChemicalElement[] {
    return this.elements.filter(e => !e.has_methods).sort((a, b) => a.symbol.localeCompare(b.symbol));
  }

  /**
   * Search elements by name or symbol
   */
  searchElements(query: string): ChemicalElement[] {
    const lowercaseQuery = query.toLowerCase();
    return this.elements.filter(e => 
      e.symbol.toLowerCase().includes(lowercaseQuery) ||
      e.name.toLowerCase().includes(lowercaseQuery)
    );
  }

  /**
   * Get element by symbol
   */
  getElementBySymbol(symbol: string): ChemicalElement | undefined {
    return this.elements.find(e => e.symbol === symbol);
  }

  /**
   * Create element combinations for common mining packages
   */
  getStandardPackages() {
    return [
      {
        id: 'precious_metals_basic',
        name: 'Package Métaux Précieux de Base',
        description: 'Analyse standard Or et Argent',
        elements: ['Au', 'Ag'],
        icon: '💎',
        popular: true
      },
      {
        id: 'base_metals_complete',
        name: 'Package Métaux de Base Complet',
        description: 'Analyse complète des métaux de base',
        elements: ['Cu', 'Pb', 'Zn', 'Ni', 'Co'],
        icon: '🔧',
        popular: true
      },
      {
        id: 'major_oxides',
        name: 'Package Oxydes Majeurs',
        description: 'Analyse des oxydes principaux',
        elements: ['Fe', 'Al', 'Ca', 'Mg', 'Si', 'K', 'Na', 'Ti', 'Mn'],
        icon: '⚗️',
        popular: false
      },
      {
        id: 'comprehensive_mining',
        name: 'Package Minier Complet',
        description: 'Analyse complète pour exploration minière',
        elements: ['Au', 'Ag', 'Cu', 'Pb', 'Zn', 'Fe', 'As', 'Sb', 'Bi'],
        icon: '🏔️',
        popular: true
      }
    ];
  }
}

export const elementGroupsService = new ElementGroupsService(); 